import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoader from './components/PageLoader';
import AuthenticatedProviders from './components/AuthenticatedProviders';
import InstallAppPrompt from './components/InstallAppPrompt';

const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const PharmacySetupPage = React.lazy(() => import('./pages/PharmacySetupPage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const PharmacyDashboard = React.lazy(() => import('./pages/pharmacy/PharmacyDashboard'));
const CustomerDashboard = React.lazy(() => import('./pages/customer/CustomerDashboard'));
const RiderDashboard = React.lazy(() => import('./pages/rider/RiderDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const CartPage = React.lazy(() => import('./pages/customer/CartPage'));
const OrdersPage = React.lazy(() => import('./pages/customer/OrdersPage'));
const EmergencyPage = React.lazy(() => import('./pages/EmergencyPage'));
const PaymentCallbackPage = React.lazy(() => import('./pages/PaymentCallbackPage'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1E5BD7',
      light: '#64B5F6',
      dark: '#1548A6',
    },
    secondary: {
      main: '#FF6F00',
      light: '#FF9800',
      dark: '#E65100',
    },
    background: {
      default: '#F5F5F5',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <InstallAppPrompt />
        <AuthProvider>
          <ErrorBoundary>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/emergency" element={<EmergencyPage />} />
                  <Route path="/payment/callback" element={<PaymentCallbackPage />} />

                  {/* Pharmacy Setup Route */}
                  <Route path="/pharmacy-setup" element={
                    <ProtectedRoute allowedUserTypes={['pharmacy']}>
                      <AuthenticatedProviders>
                        <PharmacySetupPage />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />

                  {/* Protected Routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <AuthenticatedProviders>
                        <Dashboard />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />

                  {/* Pharmacy Routes */}
                  <Route path="/pharmacy/*" element={
                    <ProtectedRoute allowedUserTypes={['pharmacy']}>
                      <AuthenticatedProviders>
                        <PharmacyDashboard />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />

                  {/* Customer Routes */}
                  <Route path="/customer/*" element={
                    <ProtectedRoute allowedUserTypes={['customer']}>
                      <AuthenticatedProviders>
                        <CustomerDashboard />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />
                  <Route path="/products" element={
                    <ProtectedRoute allowedUserTypes={['customer']}>
                      <Navigate to="/customer" replace />
                    </ProtectedRoute>
                  } />
                  <Route path="/cart" element={
                    <ProtectedRoute allowedUserTypes={['customer']}>
                      <AuthenticatedProviders>
                        <CartPage />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRoute allowedUserTypes={['customer']}>
                      <AuthenticatedProviders>
                        <OrdersPage />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />

                  {/* Rider Routes */}
                  <Route path="/rider/*" element={
                    <ProtectedRoute allowedUserTypes={['rider']}>
                      <AuthenticatedProviders>
                        <RiderDashboard />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />

                  {/* Admin Routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute allowedUserTypes={['admin']}>
                      <AuthenticatedProviders>
                        <AdminDashboard />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  } />

                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Router>
          </ErrorBoundary>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
